import { Col, Form, Row } from "react-bootstrap";
import { FormEvent, useState, useEffect } from 'react'
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from 'react-router-dom'
import "react-datepicker/dist/react-datepicker.css";
import { RoleFormDto, RolePermissionsFormDto } from "../types";
import { createRole, getRoleById, updateRole } from "../api/roleAPI";
import { InputDatePicker } from "../../../../components/InputDatePicker";
import { useLanguageUser } from "../../../../utils/auth";
import { getRoleFormSchema } from "../schema/roleSchema";
import RolePermissionsForm from "../components/FormSections/RolePermissionsForm";
import AlertMessage from "../../../../components/AlertMessage";
import { useAlert } from "../../../../hooks/useAlert";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { GenericSaveButton } from "../../../../components/common/GenericSaveButton";
import { convertUtc } from "../../../../utils/dateUtils";

export default function RolesForm() {
    const { t } = useTranslation(["permission","role"]);
    const { roleId } = useParams<{ roleId: string }>();

    const navigate = useNavigate();
    const language = useLanguageUser();
    const isEditMode = Boolean(roleId);
    const authenticatedLanguage = language || 'ca'

    const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});
    const [roleForm, setRoleForm] = useState<RoleFormDto>({
        id: 0,
        description: {},
        validityDate: null,
        rolePermissions: []
    })


    const { alert, hideAlert, isVisible } = useAlert({ fromLocation: false });

    useEffect(() => {
        if (roleId) {
            getRoleById(parseInt(roleId))
                .then(data => setRoleForm(data))
                .catch((err: ApiErrorResponse) => {
                    console.error(err);
                });
        }
    }, [roleId]);

    const handleNestedInputChange = (parentField: keyof RoleFormDto, nestedField: string) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const parentObject = roleForm[parentField] as any;

        setRoleForm({
            ...roleForm,
            [parentField]: {
                ...parentObject, 
                [nestedField]: e.target.value 
            }
        });
    };

    const handleSubmit = async (e?: FormEvent) => {
        e?.preventDefault();
        const schema = getRoleFormSchema(t, authenticatedLanguage);

        try {
            await schema.validate(roleForm, { abortEarly: false });
        } catch (err: any) {
            const validationErrors: { [key: string]: string | undefined } = {};
            err.inner?.forEach((error: any) => {
                if (error.path) {
                    validationErrors[error.path] = error.message;
                }
            });
            setErrors(validationErrors);
            return;
        }

        try {
            // Filtrar solo permisos activos (sin dateDelete)
            const activePermissions = roleForm.rolePermissions
                .filter(p => !p.dateDelete)
                .map((p) => {
                    const hasPermission = p.permission && p.permission.trim().length > 0;
                    const derived = p.section && p.action ? `${p.section}.${p.action}` : p.permission;
                    return {
                        ...p,
                        permission: hasPermission ? p.permission : (derived ?? ""),
                    };
                });

            const payload = {
                ...roleForm,
                rolePermissions: activePermissions, 
                validityDate: roleForm.validityDate
                    ? new Date(roleForm.validityDate).toISOString()
                    : null
            };

            if (isEditMode && roleId) {
                await updateRole(parseInt(roleId), payload);
                navigate('/admin/roles', {
                    state: { message: t("role:modal.messageUpdateRoleSuccessfully"), shouldRefresh: true }
                });
            } else {
                await createRole(payload);
                navigate('/admin/roles', {
                    state: { message: t("role:modal.messageCreateRoleSuccessfully"), shouldRefresh: true }
                });
            }
        } catch (e: any) {
            let errorMessage = e?.response?.data?.message || e?.message || "Ha ocurrido un error inesperado.";
            const apiErrors = e?.response?.data?.errors;

            if (Array.isArray(apiErrors)) {
                const parsedErrors: { [key: string]: string | undefined } = {};
                apiErrors.forEach((error: any) => {
                    if (error?.field) {
                        parsedErrors[error.field] = error?.message ? t(error.message) : t("common:error");
                    }
                });
                setErrors(parsedErrors);
            } else if (apiErrors && typeof apiErrors === "object") {
                const parsedErrors: { [key: string]: string | undefined } = {};
                Object.entries(apiErrors).forEach(([field, message]) => {
                    const value = Array.isArray(message) ? message[0] : message;
                    parsedErrors[field] = value ? t(String(value)) : t("common:error");
                });
                setErrors(parsedErrors);
            }

            if (errorMessage === "ERROR_DUPLICATE_USER") {
                errorMessage = t("user:errors.newUserDuplicate");
            }
            if (errorMessage === "ERROR_EMAIL_ALREADY_EXISTS") {
                errorMessage = t("user:errors.newUserExistingEmail");
            }
        }
    };

    const addPermission = (newPermission: RolePermissionsFormDto) => {
        setRoleForm(prev => {
            const exists = prev.rolePermissions.some(
                p => p.permission === newPermission.permission && !p.dateDelete
            );

            if (exists) {
                return prev;
            }

            const wasDeleted = prev.rolePermissions.some(
                p => p.permission === newPermission.permission && p.dateDelete
            );

            if (wasDeleted) {
                return {
                    ...prev,
                    rolePermissions: prev.rolePermissions.map(p =>
                        p.permission === newPermission.permission
                            ? { ...p, dateDelete: null }
                            : p
                    )
                };
            }

            return {
                ...prev,
                rolePermissions: [
                    ...prev.rolePermissions,
                    {
                        ...newPermission,
                        dateDelete: null
                    }
                ]
            };
        });
    };

    const deletePermission = (permission: RolePermissionsFormDto) => {
        setRoleForm(prev => ({
            ...prev,
            rolePermissions: prev.rolePermissions.filter(p => {
                const samePermission =
                    !!permission.permission && p.permission === permission.permission;

                const sameSectionAction =
                    p.section === permission.section &&
                    p.action === permission.action;

                return !(samePermission || sameSectionAction);
            })
        }));
    };
    return (
        <>
            <Row className="mb-2">
                <Col xs={12} md={8}>
                    <h2>
                        {roleId
                            ? t("role:updateRole")
                            : t("role:buttonNewRole")}</h2>
                </Col>
                <Col xs={4} md={4} className="text-end">
                    <GenericSaveButton
                        onClick={() => handleSubmit()}
                        onCancelClick={() => navigate('/admin/roles')}
                        cancelLabel={roleId ? t("common:buttons.buttonBack") : t("common:buttons.buttonCancel")}
                    />
                </Col>
                {isVisible && (
                    <AlertMessage
                        message={alert!.message}
                        type={alert!.type}
                        onClose={hideAlert}
                    />
                )}
            </Row>
            <Row>
                <Col>
                    <Form>
                        <Row>
                            <Col xs={4} md={4} style={{ marginBottom: "10px" }}>
                                <Form.Group>
                                    <Form.Label>{t("user:forms.newUserInputTextId")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={roleForm.id}
                                        disabled
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={6} md={6}>
                                <Form.Group className="mb-3">
                                    <InputDatePicker
                                        value={roleForm.validityDate ? new Date(roleForm.validityDate) : null}
                                        onChange={(date: Date | null) => {
                                            setRoleForm(prev => ({
                                                ...prev,
                                                validityDate: convertUtc(date) 
                                            }));
                                        }}
                                        label={t("template:edit.newTemplateInputTextEffectiveStartDate")}
                                        hasError={'validityDate' in errors && !!errors.validityDate}
                                        errorMessage={errors?.validityDate}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row style={{ marginTop: '20px' }}>
                            <Col xs={8} md={8}>
                                <Form.Group>
                                    <Form.Label>
                                        {t("genericDic:field1NewFormDescriptionCA")}
                                    </Form.Label>
                                    <Form.Control
                                        isInvalid={!!errors["description.catalan"]}
                                        type="text"
                                        name="description"
                                        value={roleForm.description?.catalan ?? ""}
                                        onChange={handleNestedInputChange('description', 'catalan')}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors["description.catalan"]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col xs={8} md={8}>
                                <Form.Group>
                                    <Form.Label>
                                        {t("genericDic:field1NewFormDescriptionES")}
                                    </Form.Label>
                                    <Form.Control
                                        isInvalid={!!errors["description.spanish"]}
                                        type="text"
                                        name="description"
                                        value={roleForm.description?.spanish ?? ""}
                                        onChange={handleNestedInputChange('description', 'spanish')}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors["description.spanish"]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col xs={8} md={8}>
                                <Form.Group>
                                    <Form.Label>
                                        {t("genericDic:field1NewFormDescriptionEN")}
                                    </Form.Label>
                                    <Form.Control
                                        isInvalid={!!errors["description.english"]}
                                        type="text"
                                        name="description"
                                        value={roleForm.description?.english ?? ""}
                                        onChange={handleNestedInputChange('description', 'english')}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors["description.english"]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col xs={8} md={8}>
                                <Form.Group>
                                    <Form.Label>
                                        {t("genericDic:field1NewFormDescriptionFR")}
                                    </Form.Label>
                                    <Form.Control
                                        isInvalid={!!errors["description.french"]}
                                        type="text"
                                        name="descripcio"
                                        value={roleForm.description?.french ?? ""}
                                        onChange={handleNestedInputChange('description', 'french')}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors["description.french"]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row style={{ marginTop: '20px' }}>
                            <Col xs={12} md={12}>
                            {errors.rolePermissions && (
                            <div className="alert alert-danger">
                                {errors["rolePermissions"]}
                            </div>
                            )}
                            </Col>
                        </Row>
                        <RolePermissionsForm
                            selectedPermissions={roleForm.rolePermissions}
                            onAddPermission={addPermission}
                            onDeletePermission={deletePermission}
                        />
                    </Form>
                </Col>
            </Row>
        </>
    );
}


